import React, { Component } from 'react';

export interface IDynamicHeightProps {
  children?: React.ReactNode;
  isExpanded?: boolean;
  className?: string;
}

interface IDynamicHeightState {
  isAnimating: boolean;
  maxHeight: number;
}

class DynamicHeight extends Component<IDynamicHeightProps, IDynamicHeightState> {
  animatingTimeout?: any;
  contentWrapper = React.createRef<HTMLDivElement>();

  constructor(props: IDynamicHeightProps) {
    super(props);

    this.state = {
      isAnimating: false,
      maxHeight: 0,
    };
  }

  componentDidMount() {
    const { isExpanded } = this.props;

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.handleHeightChange);
    }
    // If by default it is expanded, calculate max-height.
    if (isExpanded) {
      this.handleHeightChange();
    }
  }

  componentDidUpdate(prevProps: IDynamicHeightProps) {
    const { isExpanded } = this.props;

    if (isExpanded !== prevProps.isExpanded) {
      const { scrollHeight = 0 } = this.contentWrapper.current || {};
      // Set reasonable limits for animation duration.
      // Determine maxHeight at the time of the click,
      // so that it's the most accurate (consider the situation
      // when content has changed after component mounted).

      const maxHeight = isExpanded ? scrollHeight : 0;

      this.setState({
        isAnimating: true,
        maxHeight,
      });

      this.animatingTimeout = setTimeout(() => {
        this.setState({ isAnimating: false });
      }, 300);
    }
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handleHeightChange);
    }
    if (this.animatingTimeout !== undefined) {
      clearTimeout(this.animatingTimeout);
    }
  }

  handleHeightChange = () => {
    const { isExpanded } = this.props;
    const { isAnimating, maxHeight } = this.state;

    if (isExpanded && !isAnimating && this.contentWrapper !== null) {
      const { scrollHeight = 0 } = this.contentWrapper.current || {};

      if (scrollHeight !== maxHeight) {
        this.setState({ maxHeight: scrollHeight });
      }
    }
  };

  render() {
    const { isExpanded, children, ...restProps } = this.props;

    const { isAnimating, maxHeight } = this.state;

    return (
      <div
        ref={this.contentWrapper}
        style={{
          // Make overflow visible when panel is expanded in case some element
          // such as a tooltip overflows contentWrapper container.
          maxHeight,
          overflow: isExpanded && !isAnimating ? 'visible' : 'hidden',
          transition: 'max-height 300ms ease-in-out',
        }}
        {...restProps}
      >
        {children}
      </div>
    );
  }
}

export default DynamicHeight;
